/**
 * Minimal product shape consumed by the search engine. Real product types
 * (frontend `ProductResponse`, V2 API row shape) are structural supersets and
 * pass without conversion.
 */
export interface SearchableProduct {
  id: string
  name: string | null | undefined
  short_name?: string | null | undefined
  sku?: string | null | undefined
  code?: string | null | undefined
  labels?: Array<{ name?: string | null | undefined } | null> | null | undefined
  is_sleeping?: boolean | null | undefined
}

export type MatchTier = "exact" | "starts-with" | "contains" | "fuzzy" | "none"

export interface SearchResult<T extends SearchableProduct> {
  item: T
  /** Higher = better match. Tiers anchor the bands: exact=1.0, starts-with=0.9, contains=0.8, fuzzy=0.0–0.79 (1 - fuse_score). */
  score: number
  matchType: MatchTier
}

/** Per-field weights for the Fuse.js fuzzy tier. Mirrors current frontend config. */
export interface FieldWeights {
  name: number
  short_name: number
  sku: number
  code: number
  labels: number
}

export const DEFAULT_FIELD_WEIGHTS: FieldWeights = {
  name: 2,
  short_name: 1.5,
  sku: 1.5,
  code: 1,
  labels: 1,
}

export interface SearchOptions {
  /** Fuse.js fuzzy match tolerance (0 = perfect, 1 = anything). Default 0.4. */
  threshold?: number
  /** Append non-matching items at the end (frontend infinite-scroll). Default false — AI tools want only matches. */
  includeNonMatching?: boolean
  /** Expand "two" → "2" (and reverse) before matching. Default true. */
  normalizeNumbers?: boolean
  /**
   * @deprecated No-op since the shared 7-tier core landed. Kept for shape
   * parity so existing callers don't break; `tieredSearch`'s per-word Fuse
   * averaging delivers the same intent more robustly.
   */
  boostExactOrder?: boolean
  /** Include `is_sleeping=true` products. Default false. */
  includeSleeping?: boolean
  /** Cap returned results. Falsy = no cap. */
  topN?: number
  /** Substrings that must NOT appear in any searchable field. */
  exclude?: string[]
  /** Override per-field fuzzy weights. */
  fieldWeights?: Partial<FieldWeights>
}
